<template>
  <div
    class="material_assistant"
    :class="{'has-fixed-actionbar': showFixedActionBar}"
  >
    <div class="material_assistant__heading">
      <h1 class="h1--underline">
        {{ $tv('Frontend.ORing.MaterialAssis', 'ORingConfiguratorImport') }}
      </h1>
      <div>
        <NavigationClose @click="showClosePopup = true" />
      </div>
    </div>
    <Steps
      class="material_assistant__steps"
      :steps="steps"
      :current-step="materialAssistantCurrentStep"
      :completed-steps="materialAssistantCompletedSteps"
      @go-to-step="goToStep($event)"
    />
    <div class="material_assistant__subheading">
      <h2 class="h2">
        {{ steps[materialAssistantCurrentStep - 1].title }}
      </h2>
      <div
        class="toggle-switches"
        v-if="showToggleSwitches"
      >
        <!-- °C/°F toggle for steps 2 and 5 only -->
        <ToggleSwitchSquare
          v-show="materialAssistantCurrentStep === 2 || materialAssistantCurrentStep === 5"
          :is-checked="oringCalculatorFahrenheit"
          :reserve-icon-space="true"
          :label-right="$tv('fahrenheit', 'ORingConfigurator')"
          :label-left="$tv('celsius', 'ORingConfigurator')"
          @change-value="(value: boolean) => oringStore.performTemperatureUnitSwitch(value)"
        />
        <!-- mm/bar and inch/psi toggle for steps 3 and 5 only -->
        <ToggleSwitchSquare
          v-show="materialAssistantCurrentStep === 3 || materialAssistantCurrentStep === 5"
          :is-checked="oringCalculatorImperial"
          :reserve-icon-space="true"
          :label-right="tv('inchPsi', 'ORingConfigurator')"
          :label-left="tv('mmBar', 'ORingConfigurator')"
          @change-value="(value: boolean) => (oringStore.performUnitLengthSwitch(value), oringStore.performPressureSwitch(value))"
        />
      </div>
    </div>
    <component
      :is="steps[materialAssistantCurrentStep - 1].component"
      ref="stepComponentRef"
      class="comp-slot"
    />
    <div
      class="mobile-buttons-wrapper"
      :class="{ 'is-fixed': showFixedActionBar }"
    >
      <div
        class="buttons"
        :class="{'flex-end': materialAssistantCurrentStep === 1}"
      >
        <ReactiveButton
          v-show="materialAssistantCurrentStep > 1"
          :theme-color="'light'"
          :button-text="$tv('Frontend.Wizard.PreviousButtonText', 'ORingConfiguratorImport')"
          @click="goToStep(materialAssistantCurrentStep - 1)"
        />
        <ReactiveButton
          v-if="materialAssistantCurrentStep===steps.length"
          @click="showStartPopup = true"
          :is-primary-button="true"
          :button-text="$tv('startNewSearch', 'ORingConfigurator')"
        />
        <ReactiveButton
          v-else
          :theme-color="'base'"
          :button-text="nextButtonText"
          :disabled="isNextButtonDisabled"
          @click="handleNext()"
        />
      </div>
    </div>
    <PopupConfirmDialog
      :show="showClosePopup"
      :css-class="'prompt--discard'"
      :size="POPUP.Size.small"
      :headline-text="tv('discardPromptTitle', 'ORingConfigurator')"
      :text="tv('Frontend.Wizard.LeaveDescription', 'ORingConfiguratorImport')"
      :cancel-button="tv('Frontend.Wizard.Stay', 'ORingConfiguratorImport')"
      :confirmation-button="tv('Frontend.Wizard.Leave', 'ORingConfiguratorImport')"
      @close-pop-up="showClosePopup = false"
      @confirmed="closeMaterialAssistant()"
    />
    <LeaveConfirmationPopup
      :show="showStartPopup"
      :size="POPUP.Size.small"
      :title="tv('startNewSearch', 'ORingConfigurator')"
      :content="tv('startNewSearchContent', 'ORingConfigurator')"
      :stay-button-text="tv('Frontend.Button.Cancel', 'ORingConfiguratorImport')"
      :leave-button-text="tv('startNewSearch', 'ORingConfigurator')"
      @update:show="showStartPopup = $event"
      @leave="startNewSearch"
    />
  </div>
</template>

<script setup lang="ts">
import Steps from '~/components/input-components/Steps.vue'
import ReactiveButton from '~/components/ReactiveButton.vue'
import ToggleSwitchSquare from '~/components/input-components/ToggleSwitchSquare.vue'
import MaterialAssistantStep1 from '~/components/oring-calculator/material-assistant/MaterialAssistantStep1.vue'
import MaterialAssistantStep2 from '~/components/oring-calculator/material-assistant/MaterialAssistantStep2.vue'
import MaterialAssistantStep3 from '~/components/oring-calculator/material-assistant/MaterialAssistantStep3.vue'
import MaterialAssistantStep4 from '~/components/oring-calculator/material-assistant/MaterialAssistantStep4.vue'
import MaterialAssistantStep5 from '~/components/oring-calculator/material-assistant/MaterialAssistantStep5.vue'
import useI18nExtended from '~/composables/usei18nExtended'
import NavigationClose from '~/assets/icons/navigation_close.svg?component'
import PopupConfirmDialog from '~/components/popup/PopupConfirmDialog.vue'
import LeaveConfirmationPopup from '~/components/popup/LeaveConfirmationPopup.vue'
import {POPUP} from '~/assets/js/constants'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import createSeoMetaInfo from '~/assets/js/opengraph/helper'
import {getORingSeoOptions} from '~/composables/oring-helper'
import {useOringScrollToTop} from '~/composables/oring/useOringScrollToTop'
import {useOringNavigationGuard} from '~/composables/oring/useOringNavigationGuard'

// Add SEO metadata
const seoData = createSeoMetaInfo(getORingSeoOptions('material-assistant', 'Frontend.ORing.MaterialAssis', 'ORingConfiguratorImport'))
useSeoMeta(seoData)

const {tv} = useI18nExtended()
const i18n = useI18nExtended()
const route = useRoute()
const router = useRouter()
let i18nHead = useLocaleHead({addSeoAttributes: true, route: route, router: router, i18n: i18n}).value

const head = {
  ...i18nHead,
  title: i18n.tv('pageTitle', 'ORingConfigurator')
}
useHead(head)

const app = useNuxtApp()
const oringStore = useORingCalculatorStore()
const {oringCalculatorImperial, oringCalculatorFahrenheit, materialAssistantCurrentStep, materialAssistantCompletedSteps, oringCalculatorPsi} = storeToRefs(oringStore)
const {scrollToTop} = useOringScrollToTop()

// Watch for changes to isPsi and update pressure value accordingly
watch(oringCalculatorPsi, (newVal) => {
  oringStore.convertMaterialAssistantPressure(newVal)
})

const stepComponentRef = ref<any>(null)
const showClosePopup = ref(false)

useBreadcrumb([
  {
    label: tv('Frontend.ORing.Title', 'ORingConfiguratorImport'),
    path: '/oring-calculator'
  },
  {
    label: tv('Frontend.ORing.MaterialAssis', 'ORingConfiguratorImport'),
    path: '/oring-calculator/material-assistant'
  }
])

const steps = [
  {title: tv('selectChemicalMedia', 'ORingConfigurator'), component: MaterialAssistantStep1, number: 1},
  {title: tv('selectTemperature', 'ORingConfigurator'), component: MaterialAssistantStep2, number: 2},
  {title: tv('pressureAndGapWidth', 'ORingConfigurator'), component: MaterialAssistantStep3, number: 3},
  {title: tv('Frontend.Report.Application', 'ORingConfiguratorImport'), component: MaterialAssistantStep4, number: 4},
  {title: tv('summaryAndResults', 'ORingConfigurator'), component: MaterialAssistantStep5, number: 5}
]
const showStartPopup = ref(false)
const scrolledY = ref(0)
const onScroll = () => {
  scrolledY.value = window.scrollY || document.documentElement.scrollTop || 0
}

const showFixedActionBar = computed(() => {
  return materialAssistantCurrentStep.value === steps.length && scrolledY.value >= 200
})

const nextButtonText = computed(() => {
  switch (materialAssistantCurrentStep.value) {
  case 4:
    return tv('showResults', 'ORingConfigurator')
  case 5:
    return tv('startNewSearch', 'ORingConfigurator')
  default:
    return tv('Frontend.Wizard.NextButtonText', 'ORingConfiguratorImport')
  }
})

const isNextButtonDisabled = computed(() => {
  // For step 5 (last step), the button is for "Back to Start" which should always be enabled
  if (materialAssistantCurrentStep.value === 5) {
    return false
  }

  // Check if current step has validation and if it passes
  const stepC = stepComponentRef.value
  if (stepC && typeof stepC.validate === 'function') {
    return !stepC.validate()
  }

  return false
})

// Render the toggle-switch wrappers only when at least one switch is visible
const showToggleSwitches = computed(() =>
  materialAssistantCurrentStep.value === 2 ||
  materialAssistantCurrentStep.value === 3 ||
  materialAssistantCurrentStep.value === 5
)

const startNewSearch = () => {
  materialAssistantCurrentStep.value = 1
  materialAssistantCompletedSteps.value = []
  showStartPopup.value = false
  oringStore.resetMaterialAssistantStates()
  scrollToTop('auto')
}

const goToStep = async (step: number) => {
  if (step < 1 || step > steps.length) {
    return
  }
  const stepC = stepComponentRef.value ?? {validate: () => true}
  if (step <= materialAssistantCurrentStep.value) {
    materialAssistantCompletedSteps.value = materialAssistantCompletedSteps.value.filter((s: number) => s <= step)
  } else if (!materialAssistantCompletedSteps.value.includes(step - 1)) {
    if (!(await stepC.validate?.())) {
      return
    }
    materialAssistantCompletedSteps.value.push(materialAssistantCurrentStep.value)
  } else if (!(await stepC.validate?.())) {
    return
  }
  materialAssistantCurrentStep.value = step
  scrollToTop('auto')
}

const handleNext = async () => {
  if (materialAssistantCurrentStep.value < steps.length) {
    await goToStep(materialAssistantCurrentStep.value + 1)
  } else {
    // Reset for new material assistant search
    materialAssistantCurrentStep.value = 1
    materialAssistantCompletedSteps.value = []
    scrollToTop('auto')
  }
}

const {confirmLeave, clearPendingRoute} = useOringNavigationGuard(
  router,
  showClosePopup,
  '/oring-calculator/material-assistant',
  ['/oring-calculator/oring-assistant']
)

const closeMaterialAssistant = () => {
  showClosePopup.value = false
  oringStore.resetMaterialAssistantStates()

  const pendingRoute = confirmLeave()
  if (pendingRoute) {
    router.push(pendingRoute)
    clearPendingRoute()
  } else {
    navigateTo(app.$globalization.localePath('/oring-calculator'))
  }
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, {passive: true})
  onScroll()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<style lang="scss">
.prompt--discard {
  .popup-confirmation-dialog__body {
    font-weight: 300;
  }
}

.material_assistant {
  position: relative;
  margin: 0 2rem;
  min-height: 35rem;

  @include breakpoint-down($md) {
    margin: 0 1rem;
  }

  // Add bottom space only when the fixed action bar is visible
  &.has-fixed-actionbar {
    @include breakpoint-down($md) {
      padding-bottom: 7rem; // approx. bar height + spacing
    }
  }

  .h2 {
    margin: 0;
  }

  .k-button-solid-light {
    background-color: $color__fst__blue__010;
    color: $color__fst__primary;
  }

  .oring-card {
    background: $color__white;
    box-shadow: $shadow-default;
    padding: 1rem;
    min-width: 16.25rem;
    flex: 1 1 0;
    max-width: 21.875rem;
    display: flex;
    flex-direction: column;

    .inputs-radio-group {
      gap: 1rem;
    }
  }

  h3.h4 {
    margin: 0;
    margin-block: 0;
    margin-bottom: 1rem;
    font-weight: 600;
  }

  .inputs-radio-button-centered img {
    max-height: 3.5rem;
  }

  &__steps {
    margin: 1rem 0;
  }

  .h1--underline {
    margin: 0 0 2rem;
  }

  // Match mobile h1 spacing with oring-assistant: remove extra margin on small screens
  @include breakpoint-down($md) {
    .h1--underline {
      margin: 0 0 1rem;
    }
  }

  &__heading {
    display: flex;
    justify-content: space-between;
    align-items: center;

    div {
      display: flex;
      align-items: center;
      cursor: pointer;
    }

    svg.icon--svg {
      color: $color__primary;
      width: 1.875rem;
    }
  }

  &__subheading {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-direction: row;

    @include breakpoint-down($md) {
      flex-direction: column;
      align-items: flex-start;
    }

    .h2 {
      margin-top: .25rem;
      margin-bottom: .5rem;
    }

    .h3 {
      margin: 0;

      @include breakpoint-down($md) {
        margin-bottom: 1.5rem;
      }
    }

    .toggle-switches {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-direction: row;
      gap: 1rem;

      @include breakpoint-down($md) {
        /* On mobile, stack toggles as separate rows */
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: 1rem;
        margin-top: 2rem;
        width: 100%;

        /* Make each toggle fill the width so its two buttons split 50/50 */
        .toggle-switch-square {
          width: 100%;
        }

        /* On mobile, split the two buttons equally */
        .toggle-switch-square__button-left,
        .toggle-switch-square__button-right {
          width: 50%;
        }
      }

      // Remove size restrictions for toggle switches to accommodate longer labels
      .toggle-switch-square__button-left,
      .toggle-switch-square__button-right {
        width: auto;
      }
    }
  }

  .buttons {
    display: flex;
    justify-content: space-between;
    gap: 1rem;

    &.flex-end {
      justify-content: flex-end;
    }
  }

  // Mobile fixed action bar wrapper
  .mobile-buttons-wrapper {
    @include breakpoint-down($md) {
      // Default: stay in normal flow on mobile
      position: static;

      &.is-fixed {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        box-sizing: border-box;
        background: $color__white;
        padding: 1rem;
        padding-bottom: calc(1rem + env(safe-area-inset-bottom));

        /* stylelint-disable-next-line */
        box-shadow: 0 -.1rem 0.25rem rgba(0, 0, 0, 16%);
        z-index: 100;
      }
    }
  }

  .popup-content {
    padding: 1rem 0 0;
    font-weight: $base-text-font-weight-light;

    .popup-buttons {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding-top: 1rem;
    }
  }

  .popup-confirmation-dialog {
    margin: 0;

    h5 {
      margin-bottom: .5rem;
    }
  }

  h5 {
    font-size: 1.125rem;
    margin: -.5rem 0 0;
  }
}
</style>
